import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Label } from './ui/label';
import { X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const OrderModal = ({ isOpen, onClose, product }) => {
  const [formData, setFormData] = useState({
    gamerTag: '',
    platform: 'Xbox',
    availability: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [userProfile, setUserProfile] = useState(null);

  useEffect(() => {
    const fetchUserProfile = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUserProfile(response.data.user);
          // Pre-fill form with saved data
          if (response.data.user.gamer_tag) {
            setFormData(prev => ({ ...prev, gamerTag: response.data.user.gamer_tag }));
          }
          if (response.data.user.preferred_platform) {
            setFormData(prev => ({ ...prev, platform: response.data.user.preferred_platform }));
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
        }
      }
    };

    if (isOpen) {
      // Reset states when modal opens
      setIsSubmitting(false);
      setStatusMessage('');
      fetchUserProfile();
    }
  }, [isOpen]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatusMessage('Processing order...');

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const response = await axios.post(
        `${BACKEND_URL}/api/orders`,
        {
          gamer_tag: formData.gamerTag,
          platform: formData.platform,
          availability: formData.availability,
          product_name: product?.name || '',
          product_price: product?.price || '',
          stripe_url: product?.url || ''
        },
        { headers }
      );

      console.log('Order created successfully:', response.data);
      
      // Redirect immediately to Stripe using the URL from response
      const stripeUrl = response.data.stripe_url;
      if (stripeUrl) {
        console.log('Redirecting to Stripe:', stripeUrl);
        window.location.href = stripeUrl;
      } else {
        alert('Payment link not found. Please contact support.');
        setIsSubmitting(false);
        setStatusMessage('');
      }
    } catch (error) {
      console.error('Error creating order:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`Error submitting order: ${errorMsg}. Please try again or contact support at discord.gg/afkrev`);
      setIsSubmitting(false);
      setStatusMessage('');
    }
  };

  if (!product) return null;

  // Custom close handler that resets state
  const handleClose = () => {
    setIsSubmitting(false);
    setStatusMessage('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-[#0A0A0A] border border-red-500/30 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="order-modal">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-2xl font-bold uppercase font-['Rajdhani'] text-red-500">
              YOUR ORDER
            </DialogTitle>
            <button onClick={handleClose} className="text-gray-400 hover:text-white" data-testid="close-modal-button">
              <X size={24} />
            </button>
          </div>
        </DialogHeader>

        <div className="space-y-6 mt-6">
          {/* Product Info */}
          <div className="bg-black/40 border border-white/10 p-6">
            <p className="text-sm text-gray-500 uppercase tracking-wider mb-2">SELECTED PACKAGE</p>
            <h3 className="text-xl font-bold text-white mb-2">{product.name}</h3>
            <p className="text-3xl font-black text-red-500">{product.price}</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="gamerTag" className="text-sm uppercase tracking-wider text-gray-400 mb-2 block">
                GAMER TAG *
              </Label>
              <input
                id="gamerTag"
                name="gamerTag"
                type="text"
                required
                value={formData.gamerTag}
                onChange={handleChange}
                data-testid="gamer-tag-input"
                className="w-full bg-black border border-white/10 focus:border-red-500 text-white h-12 px-4 font-['Rajdhani'] focus:ring-1 focus:ring-red-500 transition-all placeholder:text-white/20"
                placeholder="Enter your gamer tag"
              />
            </div>

            <div>
              <Label htmlFor="platform" className="text-sm uppercase tracking-wider text-gray-400 mb-2 block">
                PLATFORM *
              </Label>
              <select
                id="platform"
                name="platform"
                required
                value={formData.platform}
                onChange={handleChange}
                data-testid="platform-select"
                className="w-full bg-black border border-white/10 focus:border-red-500 text-white h-12 px-4 font-['Rajdhani'] focus:ring-1 focus:ring-red-500 transition-all"
              >
                <option value="Xbox">Xbox</option>
                <option value="PlayStation">PlayStation</option>
                <option value="PC">PC</option>
              </select>
            </div>

            <div>
              <Label htmlFor="availability" className="text-sm uppercase tracking-wider text-gray-400 mb-2 block">
                AVAILABILITY *
              </Label>
              <textarea
                id="availability"
                name="availability"
                required
                value={formData.availability}
                onChange={handleChange}
                data-testid="availability-input"
                rows={4}
                className="w-full bg-black border border-white/10 focus:border-red-500 text-white px-4 py-3 font-['Rajdhani'] focus:ring-1 focus:ring-red-500 transition-all placeholder:text-white/20 resize-none"
                placeholder="Let us know your availability (timezone, preferred hours, etc.)"
              />
            </div>

            {/* Important Info */}
            <div className="bg-yellow-900/20 border border-yellow-500/30 p-4">
              <p className="text-sm text-yellow-400 font-bold uppercase tracking-wider mb-2">
                ⚠️ IMPORTANT:
              </p>
              <p className="text-sm text-gray-300 mb-2">
                You will be redirected to Stripe for secure payment. If the page doesn't open, <span className="text-yellow-400 font-bold">allow popups</span> for this site in your browser.
              </p>
              <p className="text-sm text-gray-300 mb-3">
                After payment, you MUST join our Discord to open your support ticket and receive your order:
              </p>
              <a 
                href="https://discord.gg/afkrev" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="inline-flex items-center gap-2 bg-[#5865F2] hover:bg-[#4752C4] text-white font-bold py-2 px-4 text-sm uppercase tracking-wider transition-all"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
                </svg>
                JOIN DISCORD: discord.gg/afkrev
              </a>
            </div>

            {statusMessage && (
              <div className="bg-green-900/20 border border-green-500/30 p-4 text-center">
                <p className="text-sm text-green-400 font-['Rajdhani'] uppercase tracking-wider">
                  {statusMessage}
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              data-testid="submit-order-button"
              className="w-full bg-red-600 hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold py-4 px-8 uppercase tracking-widest transition-all hover:shadow-[0_0_20px_rgba(255,0,0,0.5)]"
            >
              {isSubmitting ? 'INITIATING...' : 'PROCEED TO PAYMENT'}
            </button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OrderModal;